import React from 'react';

interface Props {
  children: React.ReactNode;
  /**
   * Called when the boundary resets itself, just before it re-renders its
   * children — the moment a caller can swap in a fresh `React.lazy` component,
   * because the old one caches its rejected import forever.
   */
  onReset?: () => void;
}

interface State {
  hasError: boolean;
}

/**
 * Catches runtime errors thrown inside the Chat tree (e.g. a Twilio client
 * that lost its token) and renders nothing instead of crashing the host page.
 * The boundary resets itself every 30 s so the chat can recover once the
 * underlying auth / token issue is resolved.
 */
export class ChatErrorBoundary extends React.Component<Props, State> {
  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(_: Props, prevState: State) {
    if (this.state.hasError && !prevState.hasError) {
      this.resetTimer = setTimeout(() => {
        this.props.onReset?.();
        this.setState({ hasError: false });
      }, 30_000);
    }
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.warn('[Chat] Caught error, hiding chat UI:', error.message);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }

  componentWillUnmount() {
    if (this.resetTimer) clearTimeout(this.resetTimer);
  }
}
